import { useMemo, useCallback, useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import _camelCase from 'lodash/camelCase';

import { ToastContext } from 'contexts';
import { utcToTimezone } from 'helpers/dates';
import { usePaginationControls } from 'helpers/hooks';
import { useApp } from 'providers/AppProvider';
import { useDashboardFollowedProjectsSearch } from '../hooks';
import { buildCsv, exportCsv } from '../utils';
import DashboardDataTable from '../DashboardDataTable';
import DashboardDataTableLinkCell from '../DashboardDataTable/DashboardDataTableLinkCell';

const DashboardFollowing = () => {
  const { t } = useTranslation();
  const { triggerToast } = useContext(ToastContext);
  const { timezone } = useApp();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState([]);
  const { paginationProps, paginationState, setTotalItems } = usePaginationControls({
    initialTotalItems: 0,
  });
  const { followedProjects, totalCount, getFollowedProjectsToExport } =
    useDashboardFollowedProjectsSearch({
      limit: paginationState.size,
      page: paginationState.page,
      search,
      sort,
    });

  useEffect(() => {
    setTotalItems(totalCount);
  }, [totalCount, setTotalItems]);

  const columns = useMemo(
    () => [
      {
        id: 'ReferenceID',
        label: t('dashboard.data_table.ref_num'),
        isSortable: true,
        render: 'string',
      },
      {
        id: 'ProjectName',
        label: t('dashboard.data_table.project_name'),
        isSortable: true,
        render: 'string',
      },
      {
        id: 'OrganizationName',
        label: t('dashboard.data_table.organization_name'),
        isSortable: true,
        render: row => row.Organization.OrganizationName,
      },
      {
        id: 'DateOpen',
        label: t('dashboard.data_table.date_project_open'),
        isSortable: true,
        render: row => utcToTimezone(row.DateOpen, { timezone, format: 'DD MMM YYYY, h:mma z' }),
      },
      {
        id: 'DateClose',
        label: t('dashboard.data_table.date_project_close'),
        isSortable: true,
        render: row => utcToTimezone(row.DateClose, { timezone, format: 'DD MMM YYYY, h:mma z' }),
      },
      {
        id: 'DateQuestionsDue',
        label: t('dashboard.data_table.date_questions_due'),
        isSortable: true,
        render: row =>
          row.DateQuestionsDue
            ? utcToTimezone(row.DateQuestionsDue, { timezone, format: 'DD MMM YYYY, h:mma z' })
            : '-',
      },
    ],
    [t, timezone]
  );

  const rowGetter = useCallback(
    async ({ search, sortBy }) => {
      // API schema expects column keys to be camel case.
      const sortByCamelCase = sortBy.map(({ id, ...rest }) => ({
        id: _camelCase(id),
        ...rest,
      }));

      setSearch(search);
      setSort(sortByCamelCase);

      // `DataTable` refetches everytime the dependencies in `rowGetter`
      // changes so this will return new projects when the search/sort are updated.
      return followedProjects;
    },
    [followedProjects]
  );

  const fileGetter = useCallback(
    () =>
      getFollowedProjectsToExport({ search, sort })
        .then(({ data }) => {
          const csv = buildCsv(data, columns);
          exportCsv(csv, {
            fileName: t('dashboard.following.data_table.export_file_name'),
            appendDate: true,
          });
        })
        .catch(() => triggerToast({ message: t('generic_error') })),
    [getFollowedProjectsToExport, search, sort, columns, t, triggerToast]
  );

  return (
    <>
      <DashboardDataTable
        count={paginationState.total}
        title={t('dashboard.following.title')}
        DataTableProps={{
          fileGetter,
          rowGetter,
          columns,
          linkRenderer: row => <DashboardDataTableLinkCell row={row} type="following" />,
          caption: t('dashboard.following.data_table.caption'),
        }}
      />
      <DashboardDataTable.Pagination
        PaginationControlsProps={{
          ...paginationProps,
          page: paginationState.page,
          totalItems: paginationState.total,
        }}
      />
    </>
  );
};

export default DashboardFollowing;
